import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';

const STATUS_LABEL = {
    chatting:            { label: 'すり合わせ中',         bg: '#e3f2fd', color: '#1565c0' },
    estimated:           { label: '見積が届いています',    bg: '#fff3e0', color: '#e65100' },
    decided:             { label: '発注済み',              bg: '#e8f5e9', color: '#2e7d32' },
    invoiced:            { label: '請求書が届いています',  bg: '#fce4ec', color: '#c62828' },
    paid:                { label: '支払い済み',            bg: '#e8f5e9', color: '#2e7d32' },
    scheduled:           { label: '工事日程確定',          bg: '#e8eaf6', color: '#283593' },
    in_progress:         { label: '工事中',                bg: '#e0f2f1', color: '#00695c' },
    completion_notified: { label: '完了確認待ち',          bg: '#fff8e1', color: '#f57f17' },
    completed:           { label: '完了',                  bg: '#f5f5f5', color: '#616161' },
    lost:                { label: 'キャンセル',            bg: '#fafafa', color: '#9e9e9e' },
};

const ACTION = {
    estimated:           { label: '見積を確認する →',           path: (id) => `/customer/estimate/${id}`,    urgent: true },
    invoiced:            { label: '請求書を確認する →',         path: (id) => `/customer/invoice/${id}`,     urgent: true },
    completion_notified: { label: '完了確認・手続きをする →',   path: (id) => `/customer/completion/${id}`,  urgent: false },
};

const WAIT_MSG = {
    chatting:    '業者とすり合わせ中',
    decided:     '請求書の到着を待っています',
    paid:        '工事日程の連絡を待っています',
    scheduled:   '工事開始を待っています',
    in_progress: '工事進行中です',
};

const ArrowIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);
const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const DECIDED_PLUS = new Set(['decided', 'invoiced', 'paid', 'scheduled', 'in_progress', 'completion_notified']);

const CustomerDashboard = () => {
    const navigate = useNavigate();
    const customerId = localStorage.getItem('customer_id');
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!customerId) return;
        api.get(`projects/by_customer/${customerId}`)
            .then(r => setProjects(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [customerId]);

    const activeProjects    = projects.filter(p => !['lost', 'completed'].includes(p.status));
    const actionNeeded      = activeProjects.filter(p => ACTION[p.status]);
    const waiting           = activeProjects.filter(p => !ACTION[p.status]);
    const hasDecidedPlus    = activeProjects.some(p => DECIDED_PLUS.has(p.status));
    const showSearchBtn     = !hasDecidedPlus && actionNeeded.length === 0;

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return (
        <div style={{ minHeight: '80vh', background: '#f5f6f8' }}>
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 16px' }}>

                {/* ページタイトル */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>ホーム</h1>
                    {showSearchBtn && (
                        <Link to="/customer/search" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '8px 16px', background: '#fff', color: '#005c53',
                                border: '1px solid #005c53', borderRadius: 4,
                                fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
                            }}>
                                <SearchIcon /> 業者を探す
                            </button>
                        </Link>
                    )}
                </div>

                {/* サマリーカード */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 28 }}>
                    {[
                        { label: '進行中', value: activeProjects.length },
                        { label: '要対応', value: actionNeeded.length, accent: actionNeeded.length > 0 },
                    ].map(({ label, value, accent }) => (
                        <div key={label} style={{
                            background: '#fff', borderRadius: 8, padding: '16px 12px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', textAlign: 'center',
                            border: accent ? '1px solid #f59e0b' : '1px solid transparent',
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 700, color: accent ? '#f59e0b' : '#005c53', lineHeight: 1, marginBottom: 6 }}>
                                {value}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{label}</div>
                        </div>
                    ))}
                </div>


                {/* プロジェクトなしの場合 */}
                {activeProjects.length === 0 && (
                    <div style={{ background: '#fff', borderRadius: 8, padding: '56px 24px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24 }}>
                        <p style={{ color: '#6b7280', fontSize: '0.95rem', marginBottom: 20 }}>
                            まだ依頼中の案件がありません
                        </p>
                        <Link to="/customer/search" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '12px 28px', background: '#005c53', color: '#fff',
                                border: 'none', borderRadius: 4, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                            }}>
                                <SearchIcon /> 業者を探して依頼する
                            </button>
                        </Link>
                    </div>
                )}

                {/* 要対応案件 */}
                {actionNeeded.length > 0 && (
                    <div style={{ marginBottom: 28 }}>
                        <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#92400e', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
                            要対応
                        </h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {actionNeeded.map(project => {
                                const action = ACTION[project.status];
                                const s = STATUS_LABEL[project.status];
                                const contractorName = project.contractor?.company_name || `業者 #${project.contractor_id}`;
                                const workType = project.condition?.work_types?.join('・') || '';
                                return (
                                    <div key={project.id} style={{
                                        background: '#fff', borderRadius: 8,
                                        borderLeft: '3px solid #f59e0b',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        padding: '18px 20px',
                                        display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between', flexWrap: 'wrap', gap: 14,
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: workType ? 6 : 0 }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.97rem', color: '#1a1a2e' }}>{contractorName}</span>
                                                <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 500, background: s?.bg, color: s?.color }}>
                                                    {s?.label}
                                                </span>
                                            </div>
                                            {workType && <p style={{ fontSize: '0.83rem', color: '#6b7280', margin: 0 }}>{workType}</p>}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                                            <button
                                                onClick={() => navigate(`/customer/chat/${project.id}`)}
                                                style={{ padding: '8px 12px', background: '#fff', color: '#6b7280', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer' }}>
                                                チャット
                                            </button>
                                            <button
                                                onClick={() => navigate(action.path(project.id))}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 5,
                                                    padding: '8px 16px',
                                                    background: action.urgent ? '#e65100' : '#005c53',
                                                    color: '#fff', border: 'none', borderRadius: 4,
                                                    fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                                                }}>
                                                {action.label}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* 待機中案件 */}
                {waiting.length > 0 && (
                    <div style={{ marginBottom: 28 }}>
                        <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#6b7280', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#d1d5db' }} />
                            進行中
                        </h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {waiting.map(project => {
                                const s = STATUS_LABEL[project.status];
                                const waitMsg = WAIT_MSG[project.status];
                                const contractorName = project.contractor?.company_name || `業者 #${project.contractor_id}`;
                                const workType = project.condition?.work_types?.join('・') || '';
                                return (
                                    <div key={project.id} style={{
                                        background: '#fff', borderRadius: 8,
                                        borderLeft: '3px solid #e0e4ea',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        padding: '18px 20px',
                                        display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between', flexWrap: 'wrap', gap: 14,
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: (workType || waitMsg) ? 6 : 0 }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.97rem', color: '#1a1a2e' }}>{contractorName}</span>
                                                {s && <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.76rem', fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>}
                                            </div>
                                            {workType && <p style={{ fontSize: '0.83rem', color: '#6b7280', margin: 0 }}>{workType}</p>}
                                            {waitMsg && <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: workType ? '4px 0 0' : 0 }}>{waitMsg}</p>}
                                        </div>
                                        <button
                                            onClick={() => navigate(`/customer/chat/${project.id}`)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 5,
                                                padding: '8px 16px', background: '#005c53', color: '#fff',
                                                border: 'none', borderRadius: 4, fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                                            }}>
                                            チャットへ <ArrowIcon />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default CustomerDashboard;
